import { ProviderMapping, SpatialExtensionProps, Subscription, TableConnectionProp } from '../types'
import { createAction, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ApiError, RequestStatus } from 'features/common'
import { RootState } from 'app/state/store'

const name = 'data_observatory'
const IS_TABLE_IN_CONNECTION_REQUEST = `${name}/check_if_table_is_in_connection_request`
const DELETE_SUBSCRIPTION_REQUEST = `${name}/delete_subscription_request`
const SUBSCRIBE_PUBLIC_REQUEST = `${name}/subscribe_public_request`
const ACCESS_SAMPLE_REQUEST = `${name}/access_sample_request`
const SUBSCRIPTIONS_FETCH_REQUEST = `${name}/fetch_subscriptions_request`
const PROVIDERS_MAPPING_FETCH_REQUEST = `${name}/fetch_providers_mapping_request`
export interface DataObservatoryState {
  samples: Subscription[]
  subscriptions: Subscription[]
  subscriptionsStatus: RequestStatus
  subscriptionsError?: ApiError | null
  providers?: ProviderMapping[]
  subscribingStatus: RequestStatus
  subscribingError?: ApiError | null
  checkTableConnectionStatus: RequestStatus
}

const initialState: DataObservatoryState = {
  samples: [],
  subscriptions: [],
  subscriptionsStatus: RequestStatus.Idle,
  subscribingStatus: RequestStatus.Idle,
  checkTableConnectionStatus: RequestStatus.Idle
}

const asyncActions = {
  checkIfTableIsInConnectionRequest: createAction<TableConnectionProp>(IS_TABLE_IN_CONNECTION_REQUEST),
  accessToSampleRequest: createAction<string>(ACCESS_SAMPLE_REQUEST),
  subscribeToPublicDatasetRequest: createAction<string>(SUBSCRIBE_PUBLIC_REQUEST),
  fetchProvidersMappingRequest: createAction(PROVIDERS_MAPPING_FETCH_REQUEST),
  fetchSubscriptionsRequest: createAction<SpatialExtensionProps & { connection: string }>(SUBSCRIPTIONS_FETCH_REQUEST),
  deleteSubscriptionRequest: createAction<string>(DELETE_SUBSCRIPTION_REQUEST)
}

const reducers = {
  setIsTableInConnectionStatus: (state: DataObservatoryState, action: PayloadAction<RequestStatus>) => {
    state.checkTableConnectionStatus = action.payload
  },
  setProviders: (state: DataObservatoryState, action: PayloadAction<ProviderMapping[]>) => {
    state.providers = action.payload
  },
  setSamples: (state: DataObservatoryState, action: PayloadAction<Subscription[]>) => {
    state.samples = action.payload.map((sub) => ({ ...sub, isSample: true }))
  },
  setSubscriptions: (state: DataObservatoryState, action: PayloadAction<Subscription[]>) => {
    state.subscriptions = action.payload
  },
  setSubscriptionsStatus: (state: DataObservatoryState, action: PayloadAction<RequestStatus>) => {
    state.subscriptionsStatus = action.payload
  },
  setSubscribingStatus: (state: DataObservatoryState, action: PayloadAction<RequestStatus>) => {
    state.subscribingStatus = action.payload
  },
  setSubscritionsError: (state: DataObservatoryState, action: PayloadAction<ApiError | null>) => {
    state.subscriptionsError = action.payload
  },
  setSubscribingError: (state: DataObservatoryState, action: PayloadAction<ApiError | null>) => {
    state.subscribingError = action.payload
  }
}

const subscriptions = (state: RootState) => state.dataObservatory.subscriptions
const slugSearching = (_, slug: string) => slug
export const getSubscriptionBySlug = createSelector(
  [subscriptions, slugSearching],
  (subscriptions: Subscription[], slug: string) =>
    subscriptions.find((subscription) => subscription.dataset_slug === slug)
)

const samples = (state: RootState) => state.dataObservatory.samples
export const getSamplesBySlug = createSelector([samples, slugSearching], (samples: Subscription[], slug: string) =>
  samples.find((sample) => sample.dataset_slug === slug)
)

export const dataExplorerSlice = createSlice({
  name,
  initialState,
  reducers
})

export const {
  setProviders,
  setSamples,
  setSubscriptions,
  setSubscriptionsStatus,
  setSubscribingStatus,
  setSubscritionsError,
  setSubscribingError,
  setIsTableInConnectionStatus
} = dataExplorerSlice.actions

export const {
  fetchSubscriptionsRequest,
  fetchProvidersMappingRequest,
  accessToSampleRequest,
  subscribeToPublicDatasetRequest,
  deleteSubscriptionRequest,
  checkIfTableIsInConnectionRequest
} = asyncActions

export default dataExplorerSlice.reducer
